/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withConfigContext } from '../../../../../../../components/ConfigContext';
import {
  Avatar,
  Card,
  Divider,
  Popconfirm,
  Row,
  Tooltip,
  Typography,
  Pagination,
} from 'antd';
import { Link } from 'react-router-dom';
import styles from '../TopGroups/styles.module.css';
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  SettingOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import GroupDevicesModal from '../GroupDevicesModal';
const { Text } = Typography;

const colors = [
  '#f0932b',
  '#badc58',
  '#6ab04c',
  '#22a6b3',
  '#0abde3',
  '#e84393',
  '#9b59b6',
  '#eb4d4b',
  '#3498db',
  '#3c6666',
  '#162828',
  '#b85a00',
  '#6815cb',
  '#9e9f8e',
  '#2bce8a',
  '#ae7350',
  '#b10000',
  '#00c21d',
  '#e90a9a',
  '#0caa5a',
];
class GroupMapping extends Component {
  constructor(props) {
    super(props);
    this.config = props.context;
    this.state = {
      refreshTable: false,
      childrenGroups: [],
      deviceGroups: [],
      childrenGroupsHistory: [],
      currentPage: 1,
      pageSize: 5,
      filters: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.children !== this.props.children) {
      this.setState({
        childrenGroups: this.props.children,
        deviceGroups: [],
        action: false,
      });
    } else if (prevProps.deviceGroups !== this.props.deviceGroups) {
      this.setState({
        deviceGroups: this.props.deviceGroups,
        action: false,
      });
    }
    if (prevProps.filters !== this.props.filters) {
      this.setState({
        filters: this.props.filters,
      });
    }
  }

  flattenGroups(groups) {
    let flattenedGroups = [];
    for (const group of groups) {
      flattenedGroups.push(group);
      if (group.childrenGroups && group.childrenGroups.length > 0) {
        const nestedGroups = this.flattenGroups(group.childrenGroups);
        flattenedGroups = flattenedGroups.concat(nestedGroups);
      }
    }
    return flattenedGroups;
  }

  handlePageChange = (page, pageSize) => {
    this.setState({
      currentPage: page,
    });
  };
  refreshDeviceCallback = status => {
    this.setState({ refreshTable: status });
  };

  openShareModal = groupId => {
    this.props.openShareModal(groupId);
  };

  onAddOperations = groupId => {
    this.props.onAddOperations(groupId);
  };

  openEditModal = group => {
    this.props.openEditModal(group);
  };

  onConfirmDeleteGroup = groupId => {
    this.props.onConfirmDeleteGroup(groupId);
  };

  onChangeSizeChanger = (current, size) => {
    this.setState({
      pageSize: size,
      currentPage: 1,
    });
  };

  render() {
    const { t, tab } = this.props;
    const { name, owner } = this.state.filters;
    const { childrenGroups, deviceGroups, currentPage, pageSize } = this.state;
    const flattenedDeviceGroups = this.flattenGroups(deviceGroups);
    let groupsToDisplay = [];

    if (tab === 'group' && (!name && !owner)) {
      groupsToDisplay = deviceGroups.map(group => ({
        ...group,
        type: 'deviceGroup',
      }));
    } else if (tab !== 'group' && (!name && !owner)) {
      groupsToDisplay = childrenGroups.map(group => ({
        ...group,
        type: 'childrenGroup',
      }));
    } else {
      groupsToDisplay = flattenedDeviceGroups
        .filter(group => {
          const nameMatch =
            !name || group.name.toLowerCase().includes(name.toLowerCase());
          const ownerMatch =
            !owner || group.owner.toLowerCase().includes(owner.toLowerCase());
          return nameMatch && ownerMatch;
        })
        .map(group => ({
          ...group,
          type: 'deviceGroup',
        }));
    }

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedGroups = groupsToDisplay.slice(startIndex, endIndex);
    const showingText = `Showing ${startIndex + 1}-${endIndex} of ${
      groupsToDisplay.length
    } groups`;
    const action = tab === 'group';
    return (
      <div>
        <div>
          <Row>
            <Link
              to={{
                pathname: `/${this.config.appName}/groups/add-group`,
              }}
              key="groups"
            >
              <Card className={styles.installCard} hoverable={true}>
                <Card.Meta
                  avatar={
                    <Avatar
                      shape="square"
                      size="large"
                      className={styles.avatar}
                      icon={<PlusOutlined />}
                    />
                  }
                  title={t('label_more_groups')}
                  description={t('label_add_more_groups')}
                />
              </Card>
            </Link>
            {paginatedGroups.map(group => {
              const avatarLetter = group.name.charAt(0).toUpperCase();
              const color = colors[(group.name.length + 10205) % 20];
              if (group.name === 'COPE' || group.name === 'BYOD') {
                return (
                  <div key={group.id}>
                    <Card className={styles.appCard} hoverable={true}>
                      <Link
                        to={{
                          pathname: `/${this.config.appName}/groups/${group.id}`,
                          state: { group },
                        }}
                        key="SubGroups"
                      >
                        <Card.Meta
                          avatar={
                            <Avatar
                              style={{
                                backgroundColor: color,
                              }}
                              shape="square"
                              size="large"
                              className={styles.avatar}
                            >
                              {avatarLetter}
                            </Avatar>
                          }
                          title={
                            <Tooltip
                              placement="topLeft"
                              title="Group Name"
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {group.name}
                              </div>
                            </Tooltip>
                          }
                          description={
                            <Tooltip
                              placement="rightBottom"
                              title="Description"
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {group.description}
                              </div>
                              <div className={styles.description}>
                                {t('label_owner')} : {group.owner}
                              </div>
                            </Tooltip>
                          }
                        />
                      </Link>
                      <div className={styles.subButtons}>
                        <GroupDevicesModal
                          groupData={group}
                          refreshTable={this.state.refreshTable}
                          refreshDeviceCallback={status =>
                            this.refreshDeviceCallback(status)
                          }
                        />
                      </div>

                      <div className={styles.subButtons2}>
                        <Link
                          to={`/${this.config.appName}/geo/history/${group.id}`}
                        >
                          <EnvironmentOutlined /> {t('label_devicesHistory')}
                        </Link>
                      </div>
                    </Card>
                  </div>
                );
              }
              if (group.name !== 'COPE' || group.name !== 'BYOD') {
                return (
                  <div key={group.id}>
                    <Card className={styles.appCard} hoverable={true}>
                      <Link
                        to={{
                          pathname: `/${this.config.appName}/groups/${group.id}`,
                          state: { group },
                        }}
                        key="SubGroups"
                      >
                        <Card.Meta
                          avatar={
                            <Avatar
                              style={{
                                backgroundColor: color,
                              }}
                              shape="square"
                              size="large"
                              className={styles.avatar}
                            >
                              {avatarLetter}
                            </Avatar>
                          }
                          title={
                            <Tooltip
                              placement="topLeft"
                              title="Group Name"
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {group.name}
                              </div>
                            </Tooltip>
                          }
                          description={
                            <Tooltip
                              placement="rightBottom"
                              title="Description"
                              autoAdjustOverflow={true}
                            >
                              <div className={styles.description}>
                                {group.description}
                              </div>
                              <div className={styles.description}>
                                {t('label_owner')} : {group.owner}
                              </div>
                            </Tooltip>
                          }
                        />
                      </Link>
                      <div className={styles.subButtons}>
                        {action && (
                          <>
                            <span>
                              <Divider type="vertical" />
                              <Tooltip
                                placement="top"
                                title={t('label_shareGroup')}
                              >
                                <a>
                                  <ShareAltOutlined
                                    onClick={() =>
                                      this.openShareModal(group.id)
                                    }
                                  />
                                </a>
                              </Tooltip>
                            </span>
                            <span>
                              <Divider type="vertical" />
                              <Tooltip
                                placement="top"
                                title={t('label_editGroup')}
                              >
                                <a>
                                  <EditOutlined
                                    onClick={() => this.openEditModal(group)}
                                  />
                                </a>
                              </Tooltip>
                            </span>
                            <span>
                              <Divider type="vertical" />
                              <Tooltip
                                placement="bottom"
                                title={t('label_deleteGroup')}
                              >
                                <Popconfirm
                                  placement="top"
                                  title={t('label_areYouSure')}
                                  onConfirm={() =>
                                    this.onConfirmDeleteGroup(group.id)
                                  }
                                  okText={t('label_ok')}
                                  cancelText={t('label_cancel')}
                                >
                                  <a>
                                    <Text type="danger">
                                      <DeleteOutlined />
                                    </Text>
                                  </a>
                                </Popconfirm>
                              </Tooltip>
                            </span>
                          </>
                        )}
                        <span>
                          <Divider type="vertical" />
                          <Tooltip
                            placement="top"
                            title={t('label_add_operation_to_this_group')}
                          >
                            <a>
                              <SettingOutlined
                                onClick={() => this.onAddOperations(group.id)}
                              />
                            </a>
                          </Tooltip>
                        </span>
                      </div>
                      <div className={styles.subButtons}>
                        <GroupDevicesModal
                          groupData={group}
                          refreshTable={this.state.refreshTable}
                          refreshDeviceCallback={status =>
                            this.refreshDeviceCallback(status)
                          }
                        />
                      </div>

                      <div className={styles.subButtons2}>
                        <Link
                          to={`/${this.config.appName}/geo/history/${group.id}`}
                        >
                          <EnvironmentOutlined /> {t('label_devicesHistory')}
                        </Link>
                      </div>
                    </Card>
                  </div>
                );
              }
            })}
          </Row>
        </div>
        <Divider />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '20px',
          }}
        >
          {showingText}
          <Pagination
            showSizeChanger
            onShowSizeChange={this.onChangeSizeChanger}
            size="small"
            current={currentPage}
            pageSize={pageSize}
            total={groupsToDisplay.length}
            onChange={this.handlePageChange}
            pageSizeOptions={['5', '10', '20', '50', '100']}
          />
        </div>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(GroupMapping));
